import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Main Application Component
 *
 * Provides:
 * - Theme configuration
 * - Authentication context (AuthProvider + UserProvider)
 * - Routing with protected routes
 * - Loading backdrop for async operations
 */
import React, { Suspense } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { UnauthenticatedTemplate } from '@azure/msal-react';
import './styles/global.scss';
import { PrivateRoute } from './PrivateRoute';
import { AppFont, AppColors } from './styles/colors';
import LoadingBackdrop from './components/LoadingBackdrop/LoadingBackdrop';
import { setupInterceptors } from './api/axiosInstance';
import { AuthProvider } from './auth';
import { UserProvider, useUser } from './context/UserContext';
import { NotificationsProvider } from './context/NotificationsContext';
import NotificationsToast from './components/NotificationsBell/NotificationsToast';
// Lazy-loaded route components for code splitting
const DashboardPage = React.lazy(() => import(/* webpackChunkName: "page-dashboard" */ './pages/Dashboard'));
const HomePage = React.lazy(() => import(/* webpackChunkName: "page-home" */ './pages/Home'));
const LoginPage = React.lazy(() => import(/* webpackChunkName: "page-login" */ './pages/login'));
const AccessDeniedPage = React.lazy(() => import(/* webpackChunkName: "page-access-denied" */ './pages/AccessDenied'));
// Set CSS variables for theme colors
if (typeof window !== 'undefined' && window.document) {
    const root = document.documentElement;
    root.style.setProperty('--color-progress-rejected', AppColors.progressRejected);
    root.style.setProperty('--color-font-on-dark', AppColors.fontOnDark);
}
const theme = createTheme({
    breakpoints: {
        values: {
            xs: 300,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    typography: {
        fontFamily: AppFont.family,
        allVariants: {
            fontFamily: AppFont.family,
        },
    },
    spacing: 8,
});
/**
 * Inner app component that has access to routing hooks
 */
const AppContent = () => {
    const navigate = useNavigate();
    const { isLoading: userLoading, accessDenied } = useUser();
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const [backdropMessage, setBackdropMessage] = React.useState(undefined);
    const showBackdrop = (message) => {
        setBackdropMessage(message || 'Loading...');
        setBackdropOpen(true);
    };
    const hideBackdrop = () => {
        setBackdropOpen(false);
        setBackdropMessage(undefined);
    };
    // Handle auth errors by redirecting to login
    const handleAuthError = React.useCallback(() => {
        // Wipe per-tab SWR cache so a re-login (possibly as a different user)
        // can't show stale data on first paint of the new session.
        import('./utils/swrCache').then(({ clearAllSWRCache }) => clearAllSWRCache());
        navigate('/login', { replace: true });
    }, [navigate]);
    // Setup axios interceptors once on mount
    React.useEffect(() => {
        try {
            setupInterceptors(showBackdrop, hideBackdrop, handleAuthError);
        }
        catch (e) {
            console.warn('Failed to setup axios interceptors', e);
        }
    }, [handleAuthError]);
    // Redirect to access-denied page if user is denied
    React.useEffect(() => {
        if (accessDenied) {
            navigate('/access-denied', { replace: true });
        }
    }, [accessDenied, navigate]);
    // Show loading while user info is being fetched
    if (userLoading) {
        return (_jsxs("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                background: 'linear-gradient(135deg, #e9f0ee 0%, #f2f3f7 100%)',
                color: '#254056',
                fontFamily: AppFont.family,
            }, children: [_jsx("h2", { style: { marginBottom: 16 }, children: "Loading..." }), _jsx("p", { children: "Please wait while we verify your access." })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingBackdrop, { open: backdropOpen, message: backdropMessage }), _jsx(NotificationsToast, {}), _jsx(Suspense, { fallback: _jsx("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh',
                        background: 'linear-gradient(135deg, #e9f0ee 0%, #f2f3f7 100%)',
                        color: '#254056',
                        fontFamily: AppFont.family,
                    }, children: _jsx("p", { children: "Loading\u2026" }) }), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: _jsx(LoginPage, {}) }), _jsx(Route, { path: "/access-denied", element: _jsx(AccessDeniedPage, {}) }), _jsx(Route, { path: "/home", element: _jsx(PrivateRoute, { component: DashboardPage }) }), _jsx(Route, { path: "/reviews", element: _jsx(PrivateRoute, { component: HomePage }) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/home" }) })] }) }), _jsx(UnauthenticatedTemplate, {})] }));
};
/**
 * Main App component
 * Wraps the application with all required providers
 * Note: MsalProvider is already set up in main.tsx
 */
const App = () => {
    return (_jsx(AuthProvider, { children: _jsx(ThemeProvider, { theme: theme, children: _jsx(UserProvider, { children: _jsx(NotificationsProvider, { children: _jsx(AppContent, {}) }) }) }) }));
};
export default App;
